import * as THREE from 'three';
import { createSeededRandom } from '../utils/random';

/**
 * Wind direction shown directly in the 3D scene instead of only on the HUD.
 *
 * Two layers read the same authoritative wind that drives the sails:
 * - `WIND STREAKS`: a single instanced draw call of thin, camera-cheap quads
 *   lying just above the water, stretched along the wind and drifting downwind.
 *   They respawn on the upwind side of the ship so the field never empties.
 * - `CLOUD WISPS`: a handful of very faint billboard sprites high overhead that
 *   crawl downwind, purely for atmosphere.
 *
 * Everything is seeded through `createSeededRandom`, matching the rest of the
 * project: no `Math.random`, so visual baselines stay reproducible.
 */

/** Number of streak instances drawn on the water surface. */
const STREAK_COUNT = 52;
/** Streaks drift inside this radius around the ship before respawning upwind. */
const STREAK_RADIUS = 100;
const STREAK_MIN_HEIGHT = 1;
const STREAK_MAX_HEIGHT = 5;
const STREAK_MIN_LENGTH = 4;
const STREAK_MAX_LENGTH = 11;
const STREAK_MIN_WIDTH = 0.18;
const STREAK_MAX_WIDTH = 0.45;

/** Cloud wisps. Horizontal radius is large enough to sit near the horizon. */
const CLOUD_COUNT = 4;
const CLOUD_MIN_HEIGHT = 200;
const CLOUD_MAX_HEIGHT = 300;
const CLOUD_MIN_SCALE = 20;
const CLOUD_MAX_SCALE = 40;
const CLOUD_WRAP_RADIUS = 1200;
const CLOUD_OPACITY = 0.14;

const SEED = 0x51e3d;

interface Streak {
  x: number;
  z: number;
  height: number;
  length: number;
  width: number;
  /** Per-streak speed multiplier so the field does not move as one rigid sheet. */
  speedScale: number;
  bobPhase: number;
  bobSpeed: number;
  bobAmount: number;
}

interface CloudWisp {
  sprite: THREE.Sprite;
  x: number;
  z: number;
  baseY: number;
  speedScale: number;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Soft radial puff used by the cloud sprites. Generated in-browser, no asset. */
function createCloudTexture(): THREE.CanvasTexture {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    const gradient = ctx.createRadialGradient(
      size / 2,
      size / 2,
      0,
      size / 2,
      size / 2,
      size / 2,
    );
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.85)');
    gradient.addColorStop(0.45, 'rgba(255, 255, 255, 0.32)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

export class WindParticles {
  private readonly rng = createSeededRandom(SEED);
  private readonly dummy = new THREE.Object3D();

  private readonly streakGeometry: THREE.PlaneGeometry;
  private readonly streakMaterial: THREE.MeshBasicMaterial;
  private readonly streaks: THREE.InstancedMesh;
  private readonly streakData: Streak[] = [];

  private readonly cloudGroup = new THREE.Group();
  private readonly cloudTexture: THREE.CanvasTexture;
  private readonly cloudMaterial: THREE.SpriteMaterial;
  private readonly clouds: CloudWisp[] = [];

  private elapsed = 0;

  constructor(private readonly scene: THREE.Scene) {
    // A unit quad rotated flat: local +X is the streak length, local Z its
    // width, and the normal points up out of the water.
    this.streakGeometry = new THREE.PlaneGeometry(1, 1, 1, 1);
    this.streakGeometry.rotateX(-Math.PI / 2);

    this.streakMaterial = new THREE.MeshBasicMaterial({
      color: 0xe8f4ff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.NormalBlending,
      fog: true,
    });

    this.streaks = new THREE.InstancedMesh(this.streakGeometry, this.streakMaterial, STREAK_COUNT);
    this.streaks.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.streaks.frustumCulled = false;
    this.streaks.name = 'wind-streaks';
    this.streaks.visible = false;
    this.scene.add(this.streaks);

    for (let i = 0; i < STREAK_COUNT; i += 1) {
      this.streakData.push(this.spawnStreak(0, 0, 1, 0));
    }
    this.writeStreakMatrices(0, 1);

    this.cloudTexture = createCloudTexture();
    this.cloudMaterial = new THREE.SpriteMaterial({
      map: this.cloudTexture,
      transparent: true,
      opacity: CLOUD_OPACITY,
      depthWrite: false,
      fog: false,
      color: 0xffffff,
    });
    this.cloudGroup.name = 'wind-clouds';
    this.scene.add(this.cloudGroup);

    for (let i = 0; i < CLOUD_COUNT; i += 1) {
      const sprite = new THREE.Sprite(this.cloudMaterial);
      const scale = CLOUD_MIN_SCALE + this.rng() * (CLOUD_MAX_SCALE - CLOUD_MIN_SCALE);
      const baseY = CLOUD_MIN_HEIGHT + this.rng() * (CLOUD_MAX_HEIGHT - CLOUD_MIN_HEIGHT);
      sprite.scale.set(scale, scale * (0.42 + this.rng() * 0.2), 1);
      sprite.position.set(0, baseY, 0);
      const cloud: CloudWisp = {
        sprite,
        x: 0,
        z: 0,
        baseY,
        speedScale: 0.6 + this.rng() * 0.8,
      };
      this.clouds.push(cloud);
      this.cloudGroup.add(sprite);
      this.placeCloud(cloud, 0, 0, 1, 0);
    }
  }

  /**
   * Advance and respawn the field around the ship.
   *
   * @param delta         Seconds since the previous update.
   * @param shipX         Ship world X, used as the centre of the streak field.
   * @param shipZ         Ship world Z.
   * @param windDirection Direction the wind blows toward, radians (vector = (cos, -sin)).
   * @param windStrength  Normalised wind strength (0 = calm, 1 = strong).
   */
  update(
    delta: number,
    shipX: number,
    shipZ: number,
    windDirection: number,
    windStrength: number,
  ): void {
    const dt = Math.min(Math.max(delta, 0), 0.1);
    const strength = clamp01(Number.isFinite(windStrength) ? windStrength : 0);
    const dir = Number.isFinite(windDirection) ? windDirection : 0;
    const windX = Math.cos(dir);
    const windZ = -Math.sin(dir);

    this.elapsed += dt;

    const streakSpeed = 5 + strength * 30;
    const radiusSquared = STREAK_RADIUS * STREAK_RADIUS;
    for (let i = 0; i < this.streakData.length; i += 1) {
      const streak = this.streakData[i];
      streak.x += windX * streakSpeed * streak.speedScale * dt;
      streak.z += windZ * streakSpeed * streak.speedScale * dt;

      const dx = streak.x - shipX;
      const dz = streak.z - shipZ;
      if (dx * dx + dz * dz > radiusSquared) {
        this.streakData[i] = this.spawnStreak(shipX, shipZ, windX, windZ);
      }
    }
    this.writeStreakMatrices(dir, 0.6 + strength * 0.9);

    // Calm air fades the streaks to nothing; strong wind makes them read clearly.
    const streakOpacity = clamp01((strength - 0.08) / 0.72) * 0.32;
    this.streakMaterial.opacity = streakOpacity;
    this.streaks.visible = streakOpacity > 0.01;

    const cloudSpeed = 1.1 + strength * 2.6;
    const wrapSquared = CLOUD_WRAP_RADIUS * CLOUD_WRAP_RADIUS;
    for (const cloud of this.clouds) {
      cloud.x += windX * cloudSpeed * cloud.speedScale * dt;
      cloud.z += windZ * cloudSpeed * cloud.speedScale * dt;

      const dx = cloud.x - shipX;
      const dz = cloud.z - shipZ;
      if (dx * dx + dz * dz > wrapSquared) {
        this.placeCloud(cloud, shipX, shipZ, windX, windZ);
      }
      cloud.sprite.position.set(cloud.x, cloud.baseY, cloud.z);
    }
  }

  dispose(): void {
    this.scene.remove(this.streaks);
    this.scene.remove(this.cloudGroup);
    this.streaks.dispose();
    this.streakGeometry.dispose();
    this.streakMaterial.dispose();
    this.cloudGroup.clear();
    this.cloudMaterial.dispose();
    this.cloudTexture.dispose();
    this.streakData.length = 0;
    this.clouds.length = 0;
  }

  /** Place a streak upwind of the ship, inside the streak radius. */
  private spawnStreak(shipX: number, shipZ: number, windX: number, windZ: number): Streak {
    const rng = this.rng;
    const dist = STREAK_RADIUS * (0.55 + rng() * 0.35);
    const offset = (rng() * 2 - 1) * (STREAK_RADIUS - dist) * 0.9;
    // Perpendicular to the wind in the XZ plane.
    const perpX = -windZ;
    const perpZ = windX;

    return {
      x: shipX - windX * dist + perpX * offset,
      z: shipZ - windZ * dist + perpZ * offset,
      height: STREAK_MIN_HEIGHT + rng() * (STREAK_MAX_HEIGHT - STREAK_MIN_HEIGHT),
      length: STREAK_MIN_LENGTH + rng() * (STREAK_MAX_LENGTH - STREAK_MIN_LENGTH),
      width: STREAK_MIN_WIDTH + rng() * (STREAK_MAX_WIDTH - STREAK_MIN_WIDTH),
      speedScale: 0.65 + rng() * 0.7,
      bobPhase: rng() * Math.PI * 2,
      bobSpeed: 0.4 + rng() * 0.8,
      bobAmount: 0.15 + rng() * 0.35,
    };
  }

  private writeStreakMatrices(windDirection: number, lengthGain: number): void {
    for (let i = 0; i < this.streakData.length; i += 1) {
      const streak = this.streakData[i];
      const bob = Math.sin(this.elapsed * streak.bobSpeed + streak.bobPhase) * streak.bobAmount;
      this.dummy.position.set(streak.x, streak.height + bob, streak.z);
      this.dummy.rotation.set(0, windDirection, 0);
      this.dummy.scale.set(streak.length * lengthGain, 1, streak.width);
      this.dummy.updateMatrix();
      this.streaks.setMatrixAt(i, this.dummy.matrix);
    }
    this.streaks.instanceMatrix.needsUpdate = true;
  }

  /** Put a cloud wisp upwind of the ship near the wrap radius. */
  private placeCloud(
    cloud: CloudWisp,
    shipX: number,
    shipZ: number,
    windX: number,
    windZ: number,
  ): void {
    const rng = this.rng;
    const dist = CLOUD_WRAP_RADIUS * (0.5 + rng() * 0.45);
    const offset = (rng() * 2 - 1) * CLOUD_WRAP_RADIUS * 0.35;
    const perpX = -windZ;
    const perpZ = windX;

    cloud.x = shipX - windX * dist + perpX * offset;
    cloud.z = shipZ - windZ * dist + perpZ * offset;
    cloud.sprite.position.set(cloud.x, cloud.baseY, cloud.z);
  }
}
